// src/components/ScheduleModal.tsx
import React from 'react';

// --- CONFIGURATION ---
type ColorKey = 'purple' | 'red' | 'green' | 'orange';

interface ColorConfig {
  bgColor: string;
  borderColor: string;
  joinTextColor: string;
}

const COLORS: Record<ColorKey, ColorConfig> = {
  purple: { bgColor: 'bg-purple-100', borderColor: 'border-purple-500', joinTextColor: 'text-purple-600' },
  red: { bgColor: 'bg-red-100', borderColor: 'border-red-500', joinTextColor: 'text-red-600' },
  green: { bgColor: 'bg-green-100', borderColor: 'border-green-500', joinTextColor: 'text-green-600' },
  orange: { bgColor: 'bg-amber-100', borderColor: 'border-amber-500', joinTextColor: 'text-amber-600' },
};

// --- DATA TYPES ---


export interface ScheduleEvent {
  id: number;
  time: string;
  roundTitle: string;
  interviewerName: string;
  colorKey: ColorKey;
}

export interface DailySchedule {
  date: string;
  events: ScheduleEvent[];
}


// --- MOCK DATA ---
export const MOCK_SCHEDULE_DATA: DailySchedule[] = [
  {
    date: 'Wed, 11th Mar 2024',
    events: [
      { id: 1, time: '10:30 AM', roundTitle: 'L1 Round : Mike Wood', interviewerName: 'Jack King', colorKey: 'purple' },
      { id: 2, time: '11:30 AM', roundTitle: 'L2 Round : John V.C', interviewerName: 'Sarah Lee', colorKey: 'red' },
      { id: 3, time: '01:00 PM', roundTitle: 'Salary Discussion : Jack...', interviewerName: 'Mike Ross', colorKey: 'green' },
      { id: 4, time: '03:00 PM', roundTitle: 'Final Round : Mike Wood', interviewerName: 'Jack King', colorKey: 'orange' },
    ],
  },
  {
    date: 'Thu, 12th Mar 2024',
    events: [
      { id: 5, time: '10:30 AM', roundTitle: 'Tech Round : John D.', interviewerName: 'Emma Stone', colorKey: 'purple' },
      { id: 6, time: '12:00 PM', roundTitle: 'HR Round : Mike Wood', interviewerName: 'Jack King', colorKey: 'green' },
    ],
  },
];

// --- COMPONENTS ---
interface ScheduleItemProps {
  event: ScheduleEvent;
  isLast: boolean;
}

const ScheduleItem: React.FC<ScheduleItemProps> = ({ event, isLast }) => {
  const config = COLORS[event.colorKey];
  return (
    <div className="flex relative">
      <div className="flex flex-col items-center mr-4">
        <div className="text-sm font-semibold text-gray-700 w-16 text-right pt-2">{event.time}</div>
        <div className={`w-3 h-3 rounded-full border-2 ${config.borderColor} ${config.bgColor}`} />
        {!isLast && <div className="w-0.5 bg-gray-300 flex-grow -mt-0.5 -mb-1" />}
      </div>

      <div className={`flex-grow p-3 mb-4 rounded-xl shadow-md ${config.bgColor} border-l-4 ${config.borderColor} flex justify-between items-center`}>
        <div>
          <h3 className="text-sm font-semibold text-gray-800">{event.roundTitle}</h3>
          <p className="text-xs text-gray-600 mt-0.5">Interviewer Name: {event.interviewerName}</p>
        </div>
        <button className={`text-xs font-bold px-3 py-1 ml-4 rounded-full ${config.joinTextColor}`} onClick={() => console.log(`Joining ${event.roundTitle}`)}>
          Join
        </button>
      </div>
    </div>
  );
};

// --- MAIN MODAL ---
interface ScheduleModalProps {
  isOpen: boolean;
  onClose: () => void;
  scheduleData: DailySchedule[];
}

const ScheduleModal: React.FC<ScheduleModalProps> = ({ isOpen, onClose, scheduleData }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900 bg-opacity-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-2xl max-h-[90vh] w-full max-w-md overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="p-5 border-b flex justify-between items-center sticky top-0 bg-white z-10">
          <h2 className="text-xl font-bold text-gray-800">Interview Schedules</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="p-6 overflow-y-auto custom-scrollbar">
          {scheduleData.map((day) => (
            <div key={day.date} className="mb-6 last:mb-0">
              <h3 className="text-sm font-bold text-blue-600 mb-4 uppercase tracking-wider">{day.date}</h3>
              {day.events.map((event, idx) => (
                <ScheduleItem key={event.id} event={event} isLast={idx === day.events.length - 1} />
              ))}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ScheduleModal;
